import { useEffect, useState } from 'react'
import styled from '@emotion/styled'
import { MenuItem, Select, FormControl, Button } from '@material-ui/core'
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown'

import FirmwareSettingsModal from './FirmwareSettingsModal'
import {
  getFirmwareUpdateOption,
  getHardwareTypeName,
  getApplyChangesButtonStatus,
  useAllowedHardware,
} from 'lib/utils/hardwareAndFirmware'
import { Firmware, FirmwareLTEDataRequest, Organization, SubTypeOfHardware, trans, TranslationGroup } from 'lib/types'
import { FirmwareGroup } from 'models/Firmware'
import {
  BORDER1_COLOR,
  BRAND_COLOR,
  GRAY3_COLOR,
  LONGLIFE_APP,
  PEERLESS_APP,
  PEERLESS_APP_V2,
  PRIMARY_COLOR,
} from 'lib/constants'
import { snakeToCamel } from 'lib/utils/casing'

const StyledHardwareTableContainer = styled.div({
  display: 'flex',
  flexDirection: 'column',
})

const StyledContainer = styled.div({
  display: 'flex',
  span: {
    flex: 1,
  },
})

const StyledLinkableText = styled.span({
  fontFamily: 'Montserrat',
  fontStyle: 'normal',
  fontWeight: 'bold',
  fontSize: '1.2rem',
  color: '#29a096',
  texTransform: 'none',
})

const StyledTitleText = styled.span({
  fontFamily: 'Montserrat',
  fontStyle: 'normal',
  fontWeight: 'bold',
  fontSize: '1.4rem',
  letterSpacing: '0.1rem',
  lineHeight: '1.5rem',
  textTransform: 'uppercase',
  color: PRIMARY_COLOR,
})

const StyledSubText = styled.span({
  fontFamily: 'Montserrat',
  fontStyle: 'normal',
  fontWeight: 'bold',
  fontSize: '1.2rem',
  lineHeight: '1.9rem',
  color: '#242424',
})

const StyledSeparator = styled.div({
  border: `1px solid ${BORDER1_COLOR}`,
  marginTop: '38px',
  marginBottom: '40px',
})

const StyledTableContent = styled.div({
  display: 'flex',
  flexDirection: 'column',
  gap: '40px',
})

const StyledFormControl = styled(FormControl)({
  background: GRAY3_COLOR,
  borderRadius: '10px',
  borderColor: BRAND_COLOR,
  textAlign: 'center',
  width: '250px',
})

const StyledButton = styled(Button)({
  color: PRIMARY_COLOR,
  padding: '8px 20px',
  border: `2px solid ${PRIMARY_COLOR}`,
  boxSizing: 'border-box',
  fontFamily: 'Montserrat',
  boxShadow: '0px 4px 8px rgba(20, 48, 72, 0.1)',
  fontSize: '1.0rem',
  fontStyle: 'normal',
  fontWeight: 'bold',
  lineHeight: '1.2rem',
  letterSpacing: '0.1rem',
  textAlign: 'left',
  marginRight: '5px',
  cursor: 'pointer',
  borderRadius: '2px',
})

/**
 * Dedupe firmware options so the dropdown is effectively a "set of versions".
 * If multiple firmware rows share the same `version` (semver) but differ in priorVersion/metadata,
 * we keep the "best" environment (prod > production > stage > staging > dev > anything else),
 * otherwise the first one wins.
 */
const dedupeFirmwaresByVersion = (firmwares: Firmware[] = []): Firmware[] => {
  const envRank: Record<string, number> = {
    prod: 100,
    production: 95,
    stage: 80,
    staging: 75,
    qa: 60,
    test: 50,
    dev: 10,
  }

  const rankEnv = (env?: string) => (env ? envRank[env.toLowerCase()] ?? 0 : 0)

  const byVersion = new Map<string, Firmware>()

  for (const fw of firmwares) {
    const v = fw?.version
    if (!v) continue

    const existing = byVersion.get(v)
    if (!existing) {
      byVersion.set(v, fw)
      continue
    }

    // Prefer the firmware entry with the higher-ranked environment
    if (rankEnv(fw.environment) > rankEnv(existing.environment)) {
      byVersion.set(v, fw)
    }
  }

  return Array.from(byVersion.values())
}

type Props = {
  org: Organization | null
  subHardware: SubTypeOfHardware[]
  firmwares: Firmware[]
  firmwaresByTypeAuto: FirmwareGroup
}

export const HardwareTable = ({ org, subHardware, firmwares, firmwaresByTypeAuto }: Props) => {
  const allowedHardware: SubTypeOfHardware[] = useAllowedHardware()
  const [firmwareSetting, setFirmwareSetting] = useState<FirmwareLTEDataRequest>({})
  const settings: TranslationGroup = trans.settings()
  const common: TranslationGroup = trans.common()
  const [confirmModalOpen, setConfirmModalOpen] = useState(false)

  const getFirmwareValue = (firmwareType: string) => {
    const savedOrgFirmware = org?.props?.firmware || {}
    const saved = savedOrgFirmware[snakeToCamel(firmwareType)] || 'notUpdate'
    return firmwareSetting[firmwareType] || saved
  }

  const renderHardwareRow = (sub: SubTypeOfHardware) => {
    const name =
      sub.name.toLowerCase() === PEERLESS_APP
        ? 'Peerless'
        : sub.name.toLowerCase() === PEERLESS_APP_V2
        ? 'Peerless2'
        : sub.name.toLowerCase() === LONGLIFE_APP
        ? 'Longlife'
        : sub.name

    const key = `${sub.hardwareType}_${sub.name}`
    const firmwareValue = getFirmwareValue(sub.firmwareType)

    // ✅ Make the dropdown a "set of versions" (dedupe on firmware.version)
    const firmwareOptions = dedupeFirmwaresByVersion(firmwaresByTypeAuto[sub.firmwareType] || [])

    return (
      <StyledContainer key={key}>
        <StyledSubText>{getHardwareTypeName(sub.hardwareType)}</StyledSubText>
        <StyledSubText>{name}</StyledSubText>
        <StyledSubText>
          <StyledFormControl>
            <Select
              id="hardware-select"
              value={firmwareValue}
              onChange={e => {
                setFirmwareSetting({
                  ...firmwareSetting,
                  [sub.firmwareType!]: e.target.value as string,
                })
              }}
              IconComponent={KeyboardArrowDownIcon}
              disableUnderline
            >
              <MenuItem value={'notUpdate'}>{settings.do_not_update}</MenuItem>

              {firmwareOptions.map(firmware => {
                return (
                  <MenuItem value={firmware.version} key={`${key}_${firmware.version}`}>
                    {getFirmwareUpdateOption(firmware)}
                  </MenuItem>
                )
              })}
            </Select>
          </StyledFormControl>
        </StyledSubText>
      </StyledContainer>
    )
  }

  const hardwareRows = allowedHardware.map(sub => {
    return renderHardwareRow(sub)
  })

  return (
    <>
      {!hardwareRows.length ? (
        <StyledLinkableText>{settings.register}</StyledLinkableText>
      ) : (
        <StyledHardwareTableContainer>
          {confirmModalOpen && (
            <FirmwareSettingsModal
              org={org}
              subType={subHardware}
              newSetting={firmwareSetting}
              firmwares={firmwares}
              closeModal={() => setConfirmModalOpen(false)}
            />
          )}

          <StyledContainer>
            <StyledTitleText>{common.hardware_type}</StyledTitleText>
            <StyledTitleText>{common.sub_type}</StyledTitleText>
            <StyledTitleText>{settings.target_version}</StyledTitleText>
          </StyledContainer>
          <StyledSeparator />
          <StyledTableContent>
            {hardwareRows}
            <StyledContainer>
              <StyledButton onClick={() => setFirmwareSetting({})}>{common.cancel}</StyledButton>
              <StyledButton
                disabled={getApplyChangesButtonStatus(firmwareSetting)}
                onClick={() => setConfirmModalOpen(true)}
              >
                {common.apply_changes}
              </StyledButton>
            </StyledContainer>
          </StyledTableContent>
        </StyledHardwareTableContainer>
      )}
    </>
  )
}
